/**
 * React hook for sending one chat turn at a time.
 *
 * `send(text)` POSTs to the streaming chat URL with the current
 * Cognito ID token, accumulates streamed token deltas, and notifies
 * the caller when the turn completes (or fails). Caller persists
 * messages via the standard SDK; this hook only owns the
 * in-flight stream.
 */

import { useCallback, useRef, useState } from "react";
import { fetchAuthSession } from "@aws-amplify/auth";

import { streamChat } from "./streamReader";

let CHAT_STREAM_URL = "";

/** Each surface (web, mobile) calls this once at boot:
 * - web reads `import.meta.env.VITE_CHAT_STREAM_URL`
 * - mobile reads `tenants/<id>/app.config.ts`'s `chatStreamUrl`
 */
export function configureChatStream(opts: { chatStreamUrl: string }): void {
  CHAT_STREAM_URL = opts.chatStreamUrl;
}

interface UseChatStreamArgs {
  estimateId: string;
  onTurnComplete: (assistantText: string, truncated: boolean) => void;
  // Spec 004 — visual picker + structured estimate.
  onOptions?: (payload: unknown) => void;
  onEstimate?: (payload: unknown) => void;
  // Spec 007 — design sketch.
  onDesign?: (payload: unknown) => void;
  // Spec 008 — photos picker.
  onPhotos?: (payload: unknown) => void;
  // Spec 014 — inline measure directive.
  onMeasure?: (payload: unknown) => void;
  // Phase 18 — add-photo directive.
  onAddPhoto?: (payload: unknown) => void;
  // Phase 18 — render directive (live spinner card).
  onRender?: (payload: unknown) => void;
  // Phase 18.7 — offer-render directive (inline "Render the design on
  // my photo" button) so the customer doesn't have to type a message
  // when render is the natural next step.
  onOfferRender?: (payload: unknown) => void;
  // Phase 19.1 — contact-Starz directive (inline Email/Call buttons)
  // when the system can't auto-price the customer's project.
  onContactStarz?: (payload: unknown) => void;
}

interface UseChatStreamReturn {
  send: (text: string) => Promise<void>;
  cancel: () => void;
  streaming: boolean;
  currentText: string;
  error: { code: string; message: string } | null;
}

export function useChatStream(args: UseChatStreamArgs): UseChatStreamReturn {
  const [streaming, setStreaming] = useState(false);
  const [currentText, setCurrentText] = useState("");
  const [error, setError] = useState<{ code: string; message: string } | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setStreaming(false);
  }, []);

  const send = useCallback(
    async (text: string) => {
      if (!CHAT_STREAM_URL) {
        setError({
          code: "MISCONFIGURED",
          message: "Chat stream URL is not configured. Set VITE_CHAT_STREAM_URL.",
        });
        return;
      }

      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();
      if (!idToken) {
        setError({ code: "UNAUTHENTICATED", message: "No active session" });
        return;
      }

      const url = `${stripTrailingSlash(CHAT_STREAM_URL)}/v1/estimates/${args.estimateId}/chat`;

      const controller = new AbortController();
      abortRef.current = controller;
      setError(null);
      setCurrentText("");
      setStreaming(true);

      let buffer = "";
      let truncated = false;

      try {
        await streamChat({
          url,
          idToken,
          body: { content: text },
          signal: controller.signal,
          onToken: (chunk) => {
            buffer += chunk;
            setCurrentText(buffer);
          },
          onDone: ({ truncated: wasTruncated }) => {
            truncated = wasTruncated;
          },
          onError: (err) => setError(err),
          onOptions: args.onOptions,
          onEstimate: args.onEstimate,
          onDesign: args.onDesign,
          onPhotos: args.onPhotos,
          onMeasure: args.onMeasure,
          onAddPhoto: args.onAddPhoto,
          onRender: args.onRender,
          onOfferRender: args.onOfferRender,
          onContactStarz: args.onContactStarz,
        });
      } finally {
        setStreaming(false);
        abortRef.current = null;
        // Always notify the caller once, even if the stream errored —
        // they get whatever buffer accumulated so the UI can persist
        // the partial assistant content if desired.
        args.onTurnComplete(buffer, truncated);
      }
    },
    [args],
  );

  return { send, cancel, streaming, currentText, error };
}

function stripTrailingSlash(url: string): string {
  return url.endsWith("/") ? url.slice(0, -1) : url;
}
